import type { MasterCatalogGroup, MasterCatalogType } from './masters';

export type EmbeddedExternalMasterMeta = {
  label: string;
  description: string;
  module?: string;
  /** When set, only shown inside matching service hub masters tab */
  serviceHub?: string;
  /** When set with serviceHub, limits to a specific sub-service slug */
  serviceSlug?: string;
};

/** Dedicated modules embedded in Master Data (not generic MasterData CRUD) */
export const EMBEDDED_EXTERNAL_MASTERS: Record<string, EmbeddedExternalMasterMeta> = {
  partner_subscription_plans: {
    label: 'Partner Subscription Plans',
    description:
      'Prepaid plans for drivers and delivery partners per zone — separate from tenant SaaS billing.',
    module: 'transportation',
  },
  restaurants: {
    label: 'Restaurant Master',
    description:
      'Full restaurant profile — owner, zone, GST/FSSAI, service modes, timing, approval, commission or subscription.',
    module: 'food',
    serviceHub: 'delivery',
    serviceSlug: 'food-delivery',
  },
};

export const COMMON_EXTERNAL_CATALOG_GROUP: MasterCatalogGroup = {
  key: '_external',
  label: 'Partners & Payout',
  description: 'Partner earning models and subscription plans',
  icon: 'CardMembership',
  types: [],
};

type EmbeddedCatalogContext = {
  embedded: boolean;
  serviceHub?: string;
  serviceSlug?: string;
  enabledModules: string[];
  isSuperAdmin: boolean;
};

function moduleAllowed(meta: EmbeddedExternalMasterMeta, ctx: EmbeddedCatalogContext): boolean {
  return !meta.module || ctx.isSuperAdmin || ctx.enabledModules.includes(meta.module);
}

function matchesEmbeddedScope(meta: EmbeddedExternalMasterMeta, ctx: EmbeddedCatalogContext): boolean {
  if (!moduleAllowed(meta, ctx)) return false;
  if (ctx.embedded) {
    if (!meta.serviceHub || meta.serviceHub !== ctx.serviceHub) return false;
    if (meta.serviceSlug && meta.serviceSlug !== ctx.serviceSlug) return false;
    return true;
  }
  return !meta.serviceHub;
}

export function getEmbeddedExternalCatalogTypes(ctx: EmbeddedCatalogContext): MasterCatalogType[] {
  return Object.entries(EMBEDDED_EXTERNAL_MASTERS)
    .filter(([, meta]) => matchesEmbeddedScope(meta, ctx))
    .map(([key, meta]) => ({
      key,
      label: meta.label,
      description: meta.description,
      module: meta.module,
      supportsHierarchy: false,
      geoFields: [],
      hasMetadata: false,
    }));
}

export function getEmbeddedExternalCatalogGroup(ctx: EmbeddedCatalogContext): MasterCatalogGroup | null {
  const types = getEmbeddedExternalCatalogTypes(ctx);
  if (!types.length) return null;
  const embedded = ctx.embedded && ctx.serviceHub === 'delivery' && ctx.serviceSlug === 'food-delivery';
  return {
    key: '_external',
    label: embedded ? 'Food Modules' : COMMON_EXTERNAL_CATALOG_GROUP.label,
    description: embedded
      ? 'Restaurant catalog and dedicated food screens'
      : COMMON_EXTERNAL_CATALOG_GROUP.description,
    icon: embedded ? 'Restaurant' : COMMON_EXTERNAL_CATALOG_GROUP.icon,
    types,
  };
}

export function isEmbeddedExternalMaster(groupKey: string, typeKey: string): boolean {
  return groupKey === '_external' && typeKey in EMBEDDED_EXTERNAL_MASTERS;
}

export function isEmbeddedExternalType(typeKey: string): boolean {
  return typeKey in EMBEDDED_EXTERNAL_MASTERS;
}

export function embeddedExternalMasterMeta(typeKey: string) {
  return EMBEDDED_EXTERNAL_MASTERS[typeKey];
}
